import { useCallback, useContext } from 'react'

import NestableClickable from 'components/Common/NestableClickable'
import OnlyOne from 'components/Common/OnlyOne'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeBetween } from 'hooks/useScreenSize'
import CompactItemCard from 'tripPlanner/components/CompactItemCard/CompactItemCard'
import DetailItemCard from 'tripPlanner/components/DetailItemCard/DetailItemCard'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import { ViewModeContext } from 'tripPlanner/contexts/ViewModeContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { setCurrentSelectionId } from 'tripPlanner/reducers/actions'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import type { ItemPresentationType } from 'tripPlanner/utils/sortable'
import type { TripItem } from 'tripPlanner/types/tripItem'
import SuggestedNote from '../SuggestionCard/SuggestedNote'
import { isFlightSearchBookmark } from 'tripPlanner/utils'
import SuggestedFlightSearch from '../SuggestionCard/SuggestedFlightSearch'
import type { DetailItemCardVariant } from '../DetailItemCard/common/types'

interface Props {
  item: TripItem
  date?: moment.Moment
  presentationType: ItemPresentationType
  variant: DetailItemCardVariant
}

function TimelineTripItem({ item, date, presentationType, variant }: Props) {
  const { viewMode } = useContext(ViewModeContext)

  const dispatch = useAppDispatch()
  const openModal = useOpenModal()
  const isTabletAndSmaller = useScreenSizeBetween('mobile', 'tablet')
  const onClick = useCallback(() => {
    if (item.type !== 'NOTE') {
      if (isTabletAndSmaller) {
        openModal(TripItemSummary, { item })
      } else {
        dispatch(setCurrentSelectionId(tripItemSelectionId(item.id)))
      }
    }
  }, [dispatch, isTabletAndSmaller, item, openModal])

  const compact = viewMode === 'compact'

  return <NestableClickable onClick={onClick}>
    <OnlyOne>
      {item.type === 'NOTE' && item.isSuggested && (
        <SuggestedNote item={item} />
      )}
      {isFlightSearchBookmark(item) && item.isSuggested && (
        <SuggestedFlightSearch item={item} />
      )}
      {compact && (
        <CompactItemCard
          item={item}
          presentationType={presentationType}
          date={date}
          variant={variant}
        />
      )}
      {!compact && (
        <DetailItemCard item={item} variant={variant} />
      )}
    </OnlyOne>
  </NestableClickable>
}

export default TimelineTripItem
